'use strict';

/**
 * Import type definitions allowing VS Code to show IntelliSense.
 *
 * @typedef {import('./CommonMethods').default} CommonMethods
 * @typedef {import('./NavigationHandler').default} NavigationHandler
 */

import ClassLogger from 'ClassLogger';
import RoughNotations from './RoughNotations';

class ConcertFilter {
    getClassName () {
        return 'ConcertFilter';
    }

    /**
     * @param {CommonMethods} commonMethods
     * @param {NavigationHandler} navigationHandler
     */
    constructor (commonMethods, navigationHandler, user, tooltip, webShareUi, trackingService) {
        this.logger = ClassLogger(self, true); // set second parameter to false to disable logging
        this.commonMethods = commonMethods;
        this.navigationHandler = navigationHandler;
        this.tooltip = tooltip;
        this.webShareUi = webShareUi;
        this.user = user;
        this.trackingService = trackingService;

        this.navigationHandler
            .on('ready', () => this.init())
            .on('render', () => this.init());
    }

    init () {
        const concertContainer = document.querySelector('[data-concerts]');
        if (!concertContainer) {
            return;
        }

        this.logger.log('Found concert calendar...');

        // Cache DOM elements
        const elements = {
            artistInput: concertContainer.querySelector('input[name="artist"]'),
            cityInput: concertContainer.querySelector('input[name="city"]'),
            concertData: concertContainer.querySelectorAll('[data-concert]'),
            resultsEl: concertContainer.querySelector('[data-concert-filter-results]'),
            tabbedContent: concertContainer.querySelector('.c-tabs'),
            tabNav: concertContainer.querySelector('.c-tabnav'),
            resetButton: concertContainer.querySelector('button[type="reset"]'),
            filterStatus: concertContainer.querySelector('#filter-status'),
        };

        // Remove duplicate concerts and clone for manipulation
        const clonedData = this.getUniqueConcerts(elements.concertData);

        // Setup input listeners
        this.setupSearchInputs(elements, clonedData);

        // Setup reset button
        this.setupResetButton(elements);
    }

    getUniqueConcerts (concertData) {
        const seen = new Set();

        return [...concertData]
            .map(node => node.cloneNode(true))
            .filter(node => {
                const key = this.getConcertKey(node);

                if (seen.has(key)) {
                    return false;
                }

                seen.add(key);
                return true;
            });
    }

    getConcertKey (node) {
        const artist = node.getAttribute('data-artist') || '';
        const cities = node.getAttribute('data-cities') || '';
        const startdate = node.getAttribute('data-startdate') || '';
        const enddate = node.getAttribute('data-enddate') || '';

        return `${artist}|${cities}|${startdate}|${enddate}`;
    }

    setupSearchInputs (elements, clonedData) {
        const { artistInput, cityInput } = elements;

        const handleInput = () => {
            this.commonMethods.debounce(() => {
                this.filterConcerts(elements, clonedData);
            }, 500)();
        };

        artistInput.addEventListener('input', handleInput);
        cityInput.addEventListener('input', handleInput);
    }

    setupResetButton (elements) {
        const { artistInput, cityInput, resultsEl, tabbedContent, resetButton, tabNav, filterStatus } = elements;

        resetButton.addEventListener('click', () => {
        // Clear form inputs
            artistInput.value = '';
            cityInput.value = '';

            // Remove dirty state
            artistInput.classList.remove('is-dirty');
            cityInput.classList.remove('is-dirty');

            // Reset display
            resultsEl.innerHTML = '';
            tabbedContent.classList.remove('u-hide');
            tabNav.classList.remove('is-disabled');

            // Announce
            filterStatus.textContent = 'Filter wurde zurückgesetzt';
        });
    }

    filterConcerts (elements, data) {
        const { artistInput, cityInput, resultsEl, tabbedContent, tabNav, filterStatus } = elements;

        const artistValue = artistInput.value.trim().toLowerCase();
        const cityValue = cityInput.value.trim().toLowerCase();

        // Hide original content while filtering
        tabbedContent.classList.add('u-hide');

        // Disable the tab navigation
        tabNav.classList.add('is-disabled');

        // Show all if both inputs are empty
        if (this.isEmptySearch(artistValue, cityValue)) {
            this.showAllConcerts(resultsEl, tabbedContent, tabNav);
            // Announce
            filterStatus.textContent = 'Filter wurde zurückgesetzt';
            return;
        }

        // Filter concerts
        const filtered = data.filter(el => this.matchesCriteria(el, artistValue, cityValue));
        // Announce
        filterStatus.textContent = filtered.length + ' Konzerte wurde gefunden.';
        // Display results
        this.displayResults(filtered, resultsEl, filterStatus);

        const payload = {
            track: 'filter',
            artist: artistValue,
            city: cityValue,
            countresults: filtered.length,
            source: 'concert-calendar',
        };
        this.commonMethods.debounce(() => {
            this.track(payload);
        }, 5000)();
    }

    isEmptySearch (artistValue, cityValue) {
        return artistValue === '' && cityValue === '';
    }

    showAllConcerts (resultsEl, tabbedContent, tabNav) {
        resultsEl.innerHTML = '';
        tabbedContent.classList.remove('u-hide');
        tabNav.classList.remove('is-disabled');
    }

    matchesCriteria (element, artistValue, cityValue) {
        const artistText = element.dataset.artist.toLowerCase();
        const citiesText = element.dataset.cities.toLowerCase();

        const cityList = citiesText.split(',').map(city => city.trim());

        const artistMatch = !artistValue || artistText.includes(artistValue);
        const cityMatch = !cityValue || cityList.some(city => city.includes(cityValue));

        return artistMatch && cityMatch;
    }

    displayResults (filtered, resultsEl, filterStatus) {
        resultsEl.innerHTML = '';

        if (filtered.length > 0) {
            filtered.forEach(el => resultsEl.appendChild(el));

            // initialize our dependent frontend components
            new RoughNotations(this.navigationHandler);
            this.tooltip.initTooltips();
            this.webShareUi.initShareButton();

            const likebuttons = resultsEl.querySelectorAll('[data-like]');
            this.user.handleLikeButton(likebuttons);
        } else {
            filterStatus.textContent = 'Keine Ergebnisse gefunden.';
            resultsEl.innerHTML = `
            <div class="u-text-center" style="width:100%;">
                <p>Leider gibt es keine Ergebnisse.</p>
            </div>
        `;
        }
    }

    track (payload) {
        this.logger.log('Sending tracking payload', payload);
        // Jentis tracking
        window._jts = window._jts || [];
        window._jts.push(payload);
        window._jts.push({ track: 'submit' });
        // Trackingservice
        try {
            this.trackingService.track('concerts', payload);
        } catch (error) {
            this.logger.error('Failed to track concert filter event', { payload, error });
        }
    }
}

export default ConcertFilter;
